import {
  createWorkerShoppableQualificationRequest,
  type GetWorkerShoppableQualificationResponse,
  getWorkerShoppableQualificationResponse,
} from "@clipboard-health/contract-backend-main";
import { API_TYPES } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { GET_WORKER_QUERY_KEY } from "@src/appV2/Worker/api/constants";
import { type Worker } from "@src/appV2/Worker/api/types";
import {
  type QueryClient,
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { generatePath } from "react-router-dom";

import { invalidateShifts } from "../../invalidateShifts";

const CREATE_SHOPPABLE_QUALIFICATION_URL = `${environmentConfig.REACT_APP_BASE_API_URL}/workers/:workerId/shoppable-qualifications`;
const GET_WORKER_RETRY_COUNT = 3;
const GET_WORKER_RETRY_DELAY_IN_MS = 250;

interface UseCreateShoppableQualificationParams {
  workerId: string;
  qualificationId: string;
}

type UseCreateShoppableQualificationOptions = Omit<
  UseMutationOptions<
    GetWorkerShoppableQualificationResponse,
    AxiosError<GetWorkerShoppableQualificationResponse>,
    UseCreateShoppableQualificationParams
  >,
  "mutationFn"
>;

export function useCreateShoppableQualification(options?: UseCreateShoppableQualificationOptions) {
  const queryClient = useQueryClient();
  const { onSuccess, ...mutationOptions } = options ?? {};

  return useMutation({
    ...mutationOptions,
    mutationFn: async (params: UseCreateShoppableQualificationParams) => {
      const response = await post({
        url: generatePath(CREATE_SHOPPABLE_QUALIFICATION_URL, {
          workerId: params.workerId,
        }),
        data: {
          data: {
            type: API_TYPES.workerShoppableQualification,
            relationships: {
              qualification: {
                data: {
                  type: "qualifications",
                  id: params.qualificationId,
                },
              },
            },
          },
        },
        requestSchema: createWorkerShoppableQualificationRequest,
        responseSchema: getWorkerShoppableQualificationResponse,
      });

      return response.data;
    },
    onSuccess: async (data, variables, context) => {
      const shoppableQualification = normalizeShoppableQualification(data);

      // Cancel any in-flight worker fetch so a stale response cannot overwrite the optimistic role.
      await queryClient.cancelQueries({
        queryKey: [GET_WORKER_QUERY_KEY],
        exact: true,
      });

      updateWorkerCacheWithShoppableQualification({
        queryClient,
        shoppableQualification,
      });

      void reconcileWorkerCacheAfterCreate({
        queryClient,
        qualificationId: variables.qualificationId,
        shoppableQualification,
      });

      // need to invalidate shifts to get the new roles in the shifts list
      void invalidateShifts(queryClient);

      await onSuccess?.(data, variables, context);
    },
  });
}

function normalizeShoppableQualification(data: GetWorkerShoppableQualificationResponse) {
  const { data: shoppableQualification } = data;

  return {
    ...shoppableQualification,
    relationships: {
      ...shoppableQualification.relationships,
      qualification: {
        data: {
          ...shoppableQualification.relationships.qualification.data,
          // we migrated `qualification` object to have singular `qualifications` type in worker-bff api so it's still incompatible with the contract-backend-main
          type: API_TYPES.qualification,
        },
      },
    },
  };
}

function updateWorkerCacheWithShoppableQualification(params: {
  queryClient: QueryClient;
  shoppableQualification: ReturnType<typeof normalizeShoppableQualification>;
}) {
  const { queryClient, shoppableQualification } = params;

  queryClient.setQueryData([GET_WORKER_QUERY_KEY], (oldData: Worker | undefined) => {
    if (!isDefined(oldData)) {
      return oldData;
    }

    const doesQualificationAlreadyExist = oldData.shoppableQualifications.some(
      (existingShoppableQualification) =>
        existingShoppableQualification.relationships.qualification.data.id ===
        shoppableQualification.relationships.qualification.data.id
    );

    if (doesQualificationAlreadyExist) {
      return oldData;
    }

    return {
      ...oldData,
      shoppableQualifications: [...oldData.shoppableQualifications, shoppableQualification],
    };
  });
}

async function reconcileWorkerCacheAfterCreate(params: {
  queryClient: QueryClient;
  qualificationId: string;
  shoppableQualification: ReturnType<typeof normalizeShoppableQualification>;
}) {
  const { queryClient, qualificationId, shoppableQualification } = params;

  try {
    const syncedWorker = await refetchWorkerUntilQualificationIsVisible({
      queryClient,
      qualificationId,
      shoppableQualification,
    });

    if (!isDefined(syncedWorker)) {
      updateWorkerCacheWithShoppableQualification({
        queryClient,
        shoppableQualification,
      });
    }
  } catch {
    updateWorkerCacheWithShoppableQualification({
      queryClient,
      shoppableQualification,
    });
  }
}

async function refetchWorkerUntilQualificationIsVisible(params: {
  queryClient: QueryClient;
  qualificationId: string;
  shoppableQualification: ReturnType<typeof normalizeShoppableQualification>;
}) {
  const { queryClient, qualificationId, shoppableQualification } = params;

  for (let attempt = 0; attempt < GET_WORKER_RETRY_COUNT; attempt += 1) {
    await queryClient.refetchQueries({
      queryKey: [GET_WORKER_QUERY_KEY],
      exact: true,
      type: "all",
    });

    const worker = queryClient.getQueryData<Worker>([GET_WORKER_QUERY_KEY]);
    if (hasWorkerQualification({ worker, qualificationId })) {
      return worker;
    }

    updateWorkerCacheWithShoppableQualification({
      queryClient,
      shoppableQualification,
    });

    if (attempt < GET_WORKER_RETRY_COUNT - 1) {
      await delay(GET_WORKER_RETRY_DELAY_IN_MS);
    }
  }

  return undefined;
}

function hasWorkerQualification(params: { worker: Worker | undefined; qualificationId: string }) {
  const { worker, qualificationId } = params;

  return worker?.shoppableQualifications.some(
    (shoppableQualification) =>
      shoppableQualification.relationships.qualification.data.id === qualificationId
  );
}

async function delay(timeoutInMilliseconds: number) {
  await new Promise((resolve) => {
    setTimeout(resolve, timeoutInMilliseconds);
  });
}
